import React, { useEffect, useId, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Lock } from "lucide-react";
import { hasSoftKeyboard } from "@/utils/softKeyboard";
import { dropCachedPreviews, unlockDocument } from "./services/unlockDocument";

interface LockedDocumentPanelProps {
  fileId: string;
}

/** What the viewer shows for a document that is still password-protected,
 *  in place of pdf.js's dialog or the sheet grid's dead end (#11856). The
 *  password entered here is stored on the document by the unlock endpoint,
 *  so it is asked once; on success the preview refetches and the document
 *  renders in place.
 *
 *  Claude Design ruling (#11856): a fixed-geometry card on the viewer's
 *  ground — icon tile, title, helper, label, field, a message slot that is
 *  always rendered at a fixed height, then a full-width button. The slot
 *  carries the wrong-password reason in red and the accepted line in gray,
 *  and the button carries progress, so nothing moves across the three
 *  states. A wrong password keeps the typed text selected in the field; no
 *  motion, since the complaint that produced this design was about things
 *  moving. */
const LockedDocumentPanel: React.FC<LockedDocumentPanelProps> = ({
  fileId,
}) => {
  const queryClient = useQueryClient();
  const inputRef = useRef<HTMLInputElement>(null);
  // Two panels can be mounted at once (the Documents overlay and the
  // interaction page's pane), so the label and message ids must not collide.
  const fieldId = useId();
  const messageId = `${fieldId}-message`;
  const [password, setPassword] = useState("");
  const [reason, setReason] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // The unlock was accepted and the preview is refetching. Normally the
  // decrypted document replaces this panel a moment later; if the panel is
  // still here, the slot says why nothing is happening.
  const [accepted, setAccepted] = useState(false);

  const submit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setBusy(true);
    setReason(null);
    try {
      const outcome = await unlockDocument(fileId, password);
      if (outcome.ok) {
        setAccepted(true);
        dropCachedPreviews(queryClient);
      } else {
        setReason(outcome.reason);
      }
    } finally {
      setBusy(false);
    }
  };

  // The common failure is one wrong character: after a rejection the typed
  // text stays, selected, with focus in the field. This runs after the
  // rejection has rendered — focusing during the request, while the button
  // is disabled and the field may be, would be a no-op.
  useEffect(() => {
    if (reason === null) return;
    const input = inputRef.current;
    if (input) {
      input.focus();
      input.select();
    }
  }, [reason]);

  const invalid = reason !== null;
  const settled = busy || accepted;

  return (
    <div className="flex min-h-full items-center justify-center px-4 py-10">
      <div className="w-full max-w-[360px] rounded-[10px] border border-gray-300 bg-white p-6">
        <div className="mb-4 flex h-10 w-10 items-center justify-center rounded-[10px] bg-[#ECEDEF]">
          <Lock className="h-5 w-5 text-[#6E7480]" aria-hidden="true" />
        </div>
        <h2 className="text-[15px] font-semibold text-[#10141B]">
          This file is password-protected.
        </h2>
        <p className="mt-1 text-[13px] leading-[19px] text-[#6E7480]">
          Once accepted, the password is saved with this file and won&rsquo;t be
          asked for again.
        </p>
        <form onSubmit={submit} className="mt-4">
          <label
            htmlFor={fieldId}
            className="mb-1.5 block text-[13px] font-medium text-[#10141B]"
          >
            Password
          </label>
          <input
            ref={inputRef}
            id={fieldId}
            type="password"
            autoComplete="off"
            // A phone or tablet keyboard covering the panel on open is not
            // what the reader came for; a keyboard-equipped device gets focus.
            autoFocus={!hasSoftKeyboard()}
            value={password}
            onChange={(event) => {
              setPassword(event.target.value);
              // Typing again withdraws the rejection: the red border and the
              // message go together, the moment the text changes.
              if (invalid) setReason(null);
            }}
            // Only an accepted unlock retires the field; a request in flight
            // keeps it live, so focus can return to it on a rejection.
            disabled={accepted}
            aria-invalid={invalid}
            aria-describedby={messageId}
            className={`h-10 w-full rounded-[10px] border px-3 text-[14px] text-[#10141B] outline-none focus:ring-2 ${
              invalid
                ? "border-[#A11B1B] focus:border-[#A11B1B] focus:ring-[#A11B1B]/20"
                : "border-gray-300 focus:border-gray-400 focus:ring-gray-800/15"
            }`}
          />
          {/* Always rendered at a fixed height, in every state, so the
              message never moves what sits above or below it. */}
          <div
            id={messageId}
            role="status"
            aria-live="polite"
            className={`mt-1.5 min-h-[18px] text-[12px] leading-[18px] ${
              invalid ? "text-[#A11B1B]" : "text-[#6E7480]"
            }`}
          >
            {reason ?? (accepted ? "Password accepted." : "")}
          </div>
          <button
            type="submit"
            disabled={settled || !password}
            className="mt-3 h-10 w-full rounded-[10px] bg-gray-800 text-[14px] font-medium text-white hover:bg-gray-900 disabled:cursor-default disabled:opacity-60"
          >
            {settled ? "Opening…" : "Unlock"}
          </button>
        </form>
      </div>
    </div>
  );
};

export default LockedDocumentPanel;
